import { StorageUsage } from './HostSettings';
import BridgeHostOptions from '../components/whatsapp/BridgeHostOptions';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantsAPI } from '../utils/api';
import toast from 'react-hot-toast';
import TenantForm from '../components/TenantForm';
import ConfirmModal from '../components/ConfirmModal';
import ImpersonateModal from '../components/ImpersonateModal';
import { useState } from 'react';
import { ArrowLeft, Loader2, Trash2, UserCheck } from 'lucide-react';

export default function TenantEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = !id;

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [impersonateConfirm, setImpersonateConfirm] = useState(false);

  // Fetch existing tenant
  const { data: tenant, isLoading: loadingTenant } = useQuery({
    queryKey: ['tenant', id],
    queryFn: async () => {
      const res = await tenantsAPI.getTenant(id!);
      return res.data.data;
    },
    enabled: !isNew,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => tenantsAPI.createTenant(data),
    onSuccess: (res) => {
      toast.success('Tenant creato con successo');
      if (res.data.storage && !res.data.storage.success) toast.error(res.data.storage.message);
      queryClient.invalidateQueries({ queryKey: ['host-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      navigate(`/tenants/${res.data.data.id}/edit`, { replace: true });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante la creazione del tenant');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: any) => tenantsAPI.updateTenant(id!, data),
    onSuccess: () => {
      toast.success('Tenant aggiornato con successo');
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      queryClient.invalidateQueries({ queryKey: ['tenant', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento del tenant');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => tenantsAPI.deleteTenant(id!),
    onSuccess: () => {
      toast.success('Tenant eliminato con successo');
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      navigate('/tenants');
    },
    onError: () => {
      toast.error('Errore durante l\'eliminazione del tenant');
    },
  });

  // Impersonate mutation
  const impersonateMutation = useMutation({
    mutationFn: ({ tenant_id, user_id }: { tenant_id: string; user_id: string }) =>
      tenantsAPI.impersonate(tenant_id, user_id),
    onSuccess: (response) => {
      const { access_token, refresh_token, tenant: tenantData, user: userData } = response.data as {
        access_token: string;
        refresh_token: string;
        user: { name: string; email: string };
        tenant: { name: string };
      };
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      toast.success(`Impersonando ${userData.name || userData.email} in ${tenantData.name}`);
      window.location.href = '/';
    },
    onError: () => {
      toast.error('Errore durante l\'impersonificazione');
    },
  });

  const handleFormSubmit = (data: any) => {
    if (isNew) {
      createMutation.mutate(data);
    } else {
      updateMutation.mutate(data);
    }
  };

  if (!isNew && loadingTenant) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/tenants')}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isNew ? 'Nuovo Tenant' : 'Modifica Tenant'}
            </h1>
            {!isNew && tenant && (
              <p className="text-sm text-gray-500">
                {tenant.nome} &middot; /{tenant.slug}
              </p>
            )}
          </div>
        </div>

        {!isNew && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setImpersonateConfirm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 text-sm"
            >
              <UserCheck className="h-4 w-4" />
              Impersona
            </button>
            <button
              onClick={() => setDeleteConfirm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 text-sm"
            >
              <Trash2 className="h-4 w-4" />
              Elimina
            </button>
          </div>
        )}
      </div>

      {/* Form */}
      <TenantForm
        tenant={isNew ? null : tenant}
        onSubmit={handleFormSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      {id && <div className="bg-white rounded-lg shadow p-6"><StorageUsage tenantId={id} /></div>}

      {id && <BridgeHostOptions key={id} tenantId={id} />}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        onConfirm={() => {
          deleteMutation.mutate();
          setDeleteConfirm(false);
        }}
        title="Conferma Eliminazione"
        message={`Sei sicuro di voler eliminare il tenant "${tenant?.nome}"?`}
        confirmLabel="Elimina"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Impersonate Modal */}
      <ImpersonateModal
        isOpen={impersonateConfirm}
        tenant={tenant || null}
        onClose={() => setImpersonateConfirm(false)}
        onConfirm={(tenantId, userId) => {
          impersonateMutation.mutate({ tenant_id: tenantId, user_id: userId });
          setImpersonateConfirm(false);
        }}
        isLoading={impersonateMutation.isPending}
      />
    </div>
  );
}
